/**
 * api.ts — le richieste e le risposte delle funzioni dei permessi edilizi (`permessiLeggi`, `permessiScrivi`; piano 5,
 * doc PERMESSI-EDILIZI-MODEL §9-§12). Stesso stile delle Gare (`shared/gare/api.ts`): l'azione dice cosa fare, la
 * richiesta si valida campo per campo (`validazione.ts`), l'azienda viene dal pass di chi chiama.
 *
 * Cosa NON esce mai verso il browser: codici fiscali e partite IVA dei soggetti (si usa il riferimento opaco `rif`),
 * lo pseudonimo del committente privato, i dati dello spazio protetto. Il nome di un professionista esce solo se
 * l'azienda l'ha sbloccato (§12.1); quello di una società sì, perché è un dato pubblico d'impresa.
 *
 * Codice condiviso fra browser e funzioni: puro, niente oltre ES2017.
 */
import type { IdCategoriaLavoro } from './categorieLavoro';
import type { VoceRiassunto } from '../aree/aree';
import type { IstanteIso } from '../gare/tipi';
import type { IdMotivo } from './interesse';
import type { Esito, Misura, TipoAtto, TitoloProfessionale, VoceCodificata } from './lettura';
import type { RuoloNellaPratica } from './pratica';
import type { AddebitoDelloSblocco } from './sblocco';
import type { RichiestaModificaOpportunita, ScritturaOpportunita } from '../crm/opportunita';
import type { IdModelloEmailPermessi, ModelloEmailSalvato } from '../crm/modelliEmail';
import type { EventoOpportunitaPermessi, OpportunitaPermessi, RiferimentoOpportunitaPermessi, StatoOpportunitaPermessi } from './opportunita';
import type { FasePratica } from './pratiche';
import type { ContattoTrovato } from '../soggetti/contattiPubblici';
import type { IdCategoriaIntervento, IdSegnale } from './vocabolario';

/** La funzione (capability) del modulo: §12. */
export const CAPABILITY_PERMESSI = 'permessi_edilizi';

/** Chi usa i Permessi edilizi: chi lavora in ufficio, come nelle Gare. Una regola per il server, il menu e i comandi. */
export const RUOLI_PERMESSI: readonly string[] = ['admin', 'manager', 'commerciale_interno', 'commerciale_esterno', 'amministrativo', 'tecnico', 'viewer'];
/** Chi può SBLOCCARE un progettista: costa all'azienda (sblocchi oltre quelli del canone), quindi chi decide le spese. */
export const RUOLI_SBLOCCO: readonly string[] = ['admin'];
/** Chi segue pratiche e progettisti (stato, passi, note): chi lavora nel modulo, non chi lo guarda soltanto. */
export const RUOLI_SEGUIRE_PERMESSI: readonly string[] = RUOLI_PERMESSI.filter((r) => r !== 'viewer');

/** Il periodo dei cantieri, in giorni dall'atto principale. Senza: tutti quelli in pubblicazione. */
export const PERIODI_CANTIERI = [30, 90, 365] as const;
export type PeriodoCantieri = (typeof PERIODI_CANTIERI)[number];

export interface FiltriCantieri {
  categorie?: IdCategoriaIntervento[];
  /** Codici catastali. */
  comuni?: string[];
  giorni?: PeriodoCantieri;
  /** Solo i cantieri che interessano sicuramente l'azienda (`adatta`): fuori anche quelli da verificare. */
  solo_adatti?: boolean;
}

/** Un soggetto della pratica come lo vede un'azienda: l'anteprima, col nome solo quando può vederlo. */
export interface AnteprimaSoggetto {
  /** Il riferimento opaco (mai il codice fiscale): con questo si apre la sua scheda. Null se il documento non lo dava. */
  rif: string | null;
  ruolo: RuoloNellaPratica;
  forma: 'societa' | 'ditta_individuale' | 'professionista';
  titolo: TitoloProfessionale | null;
  comune_studio: string | null;
  /** Il nome: di una società sempre; di un professionista o di una ditta individuale solo se sbloccato. */
  nome: string | null;
  sbloccato: boolean;
}

export interface RigaCantiere {
  /** La chiave della pratica (codice univoco della piattaforma): sicura per gli indirizzi. */
  chiave: string;
  comune: string | null;
  comune_catastale: string | null;
  provincia: string | null;
  localita: string | null;
  /** La via del cantiere solo per una società (§18). */
  indirizzo: string | null;
  tipo_atto: TipoAtto;
  fase: FasePratica;
  esito: Esito | null;
  /** AAAA-MM-GG: la data dell'atto più recente. */
  data: string | null;
  tipologie: (VoceCodificata & { categoria: IdCategoriaIntervento | null })[];
  segnali: IdSegnale[];
  lavori: string | null;
  misure: Misura[];
  /**
   * L'importo del lavoro, quando l'atto lo dice: gli atti dei comuni su un lavoro pagato da un privato (01/11/2026).
   * Facoltativo: le pratiche del SUAPE non lo hanno, e un server di prima non lo manda.
   */
  importo_lavori?: number | null;
  motivi: IdMotivo[];
  /** Interessa sicuramente l'azienda: le sue categorie di lavoro toccano i prodotti del modulo (piano 8). */
  adatta: boolean;
  /**
   * Le categorie di lavoro della pratica (dal modello e dai segnali certi del documento), o null se non è ancora
   * classificata (piano 8). Facoltativo: un server di prima non lo manda.
   */
  categorie_lavoro?: IdCategoriaLavoro[] | null;
  /** Non classificata: si mostra a tutti, da verificare (piano 8). Facoltativo come sopra. */
  da_verificare?: boolean;
  soggetti: AnteprimaSoggetto[];
  /** Da quando è un cantiere di oggi (`corrente_dal`), o la prima vista per le pratiche di prima e per quelle d'archivio. */
  prima_vista_il: IstanteIso;
  /**
   * «storico»: una pratica trovata nell'archivio dell'albo (§19.2), non un cantiere di oggi. Fra i cantieri non c'è mai;
   * nella scheda di un soggetto compare solo a chi ne vede il nome (una società, o un progettista sbloccato).
   */
  origine: 'corrente' | 'storico';
  /** L'opportunità dell'azienda su questa pratica, se la segue (§9). */
  opportunita: OpportunitaCollegataPermessi | null;
}

/** Un'opportunità vista da una riga: quanto basta per dire «la segui, è a questo punto». */
export interface OpportunitaCollegataPermessi {
  id: string;
  stato: StatoOpportunitaPermessi;
}

/**
 * L'opportunità dell'azienda su una pratica o un soggetto, con la sua storia (pannello «La tua opportunità»). La storia
 * ha un nome suo: la pratica ha già i suoi `eventi` (gli atti pubblicati).
 */
export interface OpportunitaConStoria {
  opportunita: OpportunitaPermessi | null;
  eventi_opportunita: EventoOpportunitaPermessi[];
}

export interface RispostaCantieri {
  cantieri: RigaCantiere[];
  /** Le aree dell'azienda come etichette (`riassunto`); vuoto se non ne ha. */
  aree_usate: VoceRiassunto[];
  /** L'azienda non ha ancora scelto aree: la pagina la porta a sceglierle. */
  senza_aree: boolean;
  /** Quanti comuni delle aree hanno l'albo letto ogni giorno (gli altri non danno ancora cantieri). */
  comuni_letti: number;
  comuni_nelle_aree: number;
}

export interface EventoDellaPratica {
  tipo_atto: TipoAtto;
  numero: string | null;
  data: string | null;
  esito: Esito | null;
  /** La pagina dell'albo da cui viene, mai il documento con i dati personali. */
  url_origine: string | null;
}

export interface DettaglioPratica extends RigaCantiere {
  codice_univoco: string | null;
  numero_provvedimento: string | null;
  data_provvedimento: string | null;
  tipologia_iter: string | null;
  endoprocedimenti: VoceCodificata[];
  destinazione: string | null;
  oneri: { costo_costruzione: number | null; urbanizzazione: number | null };
  eventi: EventoDellaPratica[];
}

export interface RispostaPratica extends OpportunitaConStoria {
  pratica: DettaglioPratica;
}

/** La scheda di un soggetto nei Permessi: l'anteprima (§12.1) e le sue pratiche. */
export interface SchedaSoggettoPermessi extends AnteprimaSoggetto {
  ruoli: RuoloNellaPratica[];
  pratiche_totali: number;
  /** AAAA-MM-GG dell'atto più recente. */
  ultima_il: string | null;
  /** Dal più attivo: nome del comune e numero di pratiche. */
  comuni: { comune: string; pratiche: number }[];
  /** Quante pratiche per tipo di intervento. */
  categorie: { categoria: IdCategoriaIntervento; pratiche: number }[];
  /** Quante delle sue pratiche cadono nelle aree dell'azienda. */
  nelle_aree: number;
  /**
   * I contatti pubblici trovati su Google e sul web (piano 6), con la regola del nome: una società subito, un progettista
   * dopo lo sblocco; null = non si vedono. Un elenco vuoto = cercati (o da cercare) senza risultati.
   */
  contatti_trovati: ContattoTrovato[] | null;
}

export interface RispostaSoggetto extends OpportunitaConStoria {
  soggetto: SchedaSoggettoPermessi;
  /** Le sue pratiche, dalla più recente (con lo stesso filtro dei nomi delle righe dei cantieri). */
  pratiche: RigaCantiere[];
}

export type RichiestaLeggiPermessi =
  | { azione: 'cantieri'; filtri?: FiltriCantieri }
  | { azione: 'pratica'; chiave: string }
  | { azione: 'soggetto'; rif: string }
  /** I modelli delle email che l'azienda ha cambiato (`crm/modelliEmail`): gli altri sono quelli di partenza. */
  | { azione: 'modelli_email' };

export interface RispostaModelliEmail {
  modelli: ModelloEmailSalvato[];
}

/**
 * Chi cambia i modelli delle email dell'azienda nei Permessi: chi la guida. Gli altri ritoccano ogni email prima di
 * mandarla, ma non il modello di tutti (scelta del 30/10/2026; nelle Gare sono i ruoli del profilo).
 */
export const RUOLI_MODELLI_PERMESSI: readonly string[] = ['admin'];

/**
 * Lo sblocco di un progettista visto PRIMA di confermare (§12.1, passo 5.3): si può e cosa costa (compreso nei sblocchi
 * del mese, extra in fattura, senza addebito), oppure perché no. La stessa regola che applica lo sblocco vero.
 */
export type AnteprimaSblocco =
  | { stato: 'si_puo'; addebito: Exclude<AddebitoDelloSblocco, { tipo: 'oltre_il_tetto' }> }
  | { stato: 'gia_sbloccato' }
  /** Una società: il nome si vede già, non c'è niente da sbloccare. */
  | { stato: 'non_serve' }
  /** Lo strumento del modulo non è ancora nel listino (prezzi da decidere): lo sblocco non si vende. */
  | { stato: 'non_in_vendita' }
  /** Il tetto di spesa del mese scelto dall'azienda non regge un altro sblocco in più. */
  | { stato: 'oltre_il_tetto'; tetto_eur: number };

/** Il costo che la persona ha visto e confermato: si sblocca solo se è ancora quello. */
export interface AttesoSblocco {
  tipo: 'compreso' | 'extra' | 'senza_addebito';
  /** Il prezzo dell'extra visto (IVA esclusa); null per gli altri. */
  prezzo_eur: number | null;
}

export interface RispostaAnteprimaSblocco {
  anteprima: AnteprimaSblocco;
}

export interface RispostaSblocco {
  /** Il progettista è sbloccato (adesso o già da prima). */
  sbloccato: boolean;
  /** Il costo non era più quello visto (la quota è finita nel frattempo): niente sblocco, c'è l'anteprima nuova. */
  cambiato: boolean;
  /** Lo stato dopo la richiesta: `gia_sbloccato` se è andata, altrimenti il perché o il costo nuovo. */
  anteprima: AnteprimaSblocco;
  /** La scheda del soggetto riletta: col nome se sbloccato. */
  scheda: RispostaSoggetto;
}

/** La risposta di ogni scrittura su un'opportunità: il documento riletto e la sua storia. */
export interface RispostaOpportunitaPermessi {
  opportunita: OpportunitaPermessi;
  eventi: EventoOpportunitaPermessi[];
  gia_fatto: boolean;
}

export type RichiestaScriviPermessi =
  | { azione: 'anteprima_sblocco'; rif: string }
  | { azione: 'sblocca'; rif: string; atteso: AttesoSblocco }
  /** Seguire una pratica o un soggetto (il riferimento opaco, mai il codice fiscale). */
  | (ScritturaOpportunita & { azione: 'segui'; riferimento: RiferimentoOpportunitaPermessi })
  /** Stato, nota, passi, contatto: le modifiche comuni a ogni modulo, con gli stati dei Permessi. */
  | RichiestaModificaOpportunita<StatoOpportunitaPermessi>
  /** Un modello delle email dei Permessi: null = il testo di partenza (`crm/modelliEmail`). */
  | (ScritturaOpportunita & { azione: 'salva_modello_email'; id: IdModelloEmailPermessi; oggetto: string | null; corpo: string | null });
